/**
 * Applications API — contract #3/#4/#5 (docs/05-dev-spec.md §4, task T.C3).
 *
 *   #3 GET  /api/applications          → `{ data: { applications: [...] }, meta: { page, pageSize, total } }`
 *   #4 GET  /api/applications/:pguId   → `{ data: ApplicationDto & { events: [...] } }`
 *   #5 PATCH /api/applications/:pguId  → `{ data: ApplicationDto }`
 *
 * The shapes are mirrored from the REAL api module (`apps/api/src/applications/dto.ts`),
 * which mirrors the generated `packages/shared/src/openapi/api-types.ts` 1:1 — the
 * generated file is not reachable from here (`@cb-manager/shared` exports only its
 * barrel), so the mirror lives in one place: this file. Everything the page needs
 * beyond the DTO (filters → query string, sort vocabulary, PATCH validation) is
 * pure and lives in `pages/applications/applications-model.ts` / `-form.ts`.
 *
 * T.R2 removed the per-role mask: every authenticated role reads the same clear
 * `fullName/phone/email`, and this layer passes them through untouched.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AppStatus } from '@cb-manager/shared';

import { buildQuery, requestEnvelope, type Envelope } from './client';

// ADDITIVE (v1.8.0, Р9/D8, T.Y10) — type-only, so the edge `applications →
// manager-statuses` never exists at runtime and the module graph stays one-way.
import type { ApplicationManagerStatusDto } from './manager-statuses';

// ADDITIVE (v1.9.56, T.Z186) — the VALUE of the mailings client's root constant only:
// the register's «Рассылка» select reads the SAME #34.1 index the mailings page does,
// so the path is borrowed instead of re-spelled. The hook lives HERE (the register's
// own surface), not in `./mailing-lists` — a sibling track's file.
import { MAILING_LISTS_ROOT } from './mailing-lists';

// ---------------------------------------------------------------------------
// Enums (openapi `components.schemas`, upper-snake on the wire)
// ---------------------------------------------------------------------------

export type ApplicationStatus = AppStatus;
export type VenueMatch = 'UNMATCHED' | 'AUTO' | 'MANUAL';
export type ApplicationEventType = 'STATUS_CHANGED' | 'FIELD_UPDATED' | 'FLAG_SET';
/** ADDITIVE (v1.9.38, T.U1) — `UNTI_API` is the cat.2035.university sweep's provenance. */
export type EventSource = 'SNAPSHOT' | 'FTP' | 'UNTI_API' | 'MANUAL' | 'SEED';
export type EventActorType = 'SYSTEM' | 'USER';
/** The canonical learning format — the ЕПГУ «Формат» column (T.F1, migration 0006). */
export type CourseFormat = 'OFFLINE' | 'ONLINE';

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export interface ApplicationDto {
  /** Cross-source key — a 10-digit string starting with `7`/`8`, or `null` for the FTP-only row. */
  pguId: string | null;
  untiId: string | null;
  courseId: number | null;
  venueId: number | null;
  venueName: string | null;
  venueMatchStatus: VenueMatch;
  region: string | null;
  /** Masked for VIEWER, clear for MANAGER/ADMIN. */
  fullName: string | null;
  phone: string | null;
  email: string | null;
  isOrganic: boolean;
  agreementRejected: boolean;
  /**
   * ADDITIVE (v1.9.15, T.Z96) — the manual «не прошёл ЕВИ» flag, set BY HAND through
   * #5 (MANAGER+, reversible). NOT a funnel status: `status` keeps deriving from the
   * presence flags, so the card renders its own chip next to the derived status.
   */
  eviFailedManually: boolean;
  /**
   * ADDITIVE (v1.9.25, T.Z113) — the AUTO «Провалил ЕВИ» fact: the worker's registry
   * engine marked this child absent from the freshest ЕВИ pass list with no FTP
   * evidence, and clears it when the child reappears. NOT a funnel status; the chip
   * folds it with `eviFailedManually` (ONE chip, shared `eviFailedChip`).
   */
  eviFailed: boolean;
  // ---- ADDITIVE (v1.9.21, T.Z101) — the contract facts the worker's sync writes. ----
  /** The child appeared on the iSmart `future` platform («пришёл в iSmart»). */
  contractArrived: boolean;
  /** Arrived AND the platform marks the enrollment contract signed. */
  contractSigned: boolean;
  /** The platform's own signing instant; `null` while not signed. */
  contractSignedAt: string | null;
  /** Passed the ЕВИ but absent from the platform — the обзвон bucket. */
  contractNotArrived: boolean;
  /**
   * The display fold of the four facts: `SIGNED` / `NOT_SIGNED` / `NOT_ARRIVED`, or
   * `null` = «the sync has not run for this row yet» (the honest «—», never a guessed
   * «не подписан»). NOT a funnel status — a second axis next to the derived one.
   */
  contractState: 'SIGNED' | 'NOT_SIGNED' | 'NOT_ARRIVED' | null;
  // ---- ADDITIVE (v1.9.24, T.Z111) — the enrollment facts the same worker sync writes
  // ---- off the platform's `application` table (migration 0031). ----
  /** The platform keeps an `application` row of this child with `status='ACCEPTED'`. */
  enrollAccepted: boolean;
  /** The row's OWN submission's platform word, read by its (unti, pgu) pair (T.Z242) — «Отказано в зачислении». */
  enrollDeclined: boolean;
  /**
   * The display fold of the two: `ENROLLED` / `DECLINED`, or `null` = «the sync has
   * reported no enrollment fact for this row» (the honest «—»). Its own field, NOT a
   * `contractState` member — that field is the frozen contract-document axis.
   */
  enrollState: 'ENROLLED' | 'DECLINED' | null;
  /**
   * ADDITIVE (v1.9.49, T.Z169) — «Получено согласие родителей»: the applicant's own
   * checkbox on the platform, copied by the same worker sync (migration 0040).
   * TRI-STATE, always serialised: `true` = «Отмечено», `false` = «Не отмечено`,
   * `null` = «нет данных» — the sync has no live platform row for the child, which
   * is never a «не отмечено» answer (the `?legalRepConsent=` filter matches a `null`
   * row never).
   */
  legalRepConsent?: boolean | null;
  /**
   * ADDITIVE (v1.9.51, T.Z172) — the iSmart signer's display name (the platform
   * account holder who pressed «Подписать»), copied by the same worker sync
   * (migration 0041). PII stored on the row BY OWNER DECISION 23.09 and rendered on
   * the drawer only — never a log, never an event, never an export column. `null` =
   * their user row or its name parts are missing. ALWAYS serialised.
   */
  signerName?: string | null;
  /**
   * ADDITIVE (v1.9.51, T.Z172) — «Договор заключен ПП?»: does the signer's
   * «Фамилия Имя» fold to our child's first two tokens? TRI-STATE, always
   * serialised: `true` = «Да» (calm), `false` = «Нет» (DANGER — somebody else
   * signed), `null` = no verdict (no signer, no our name, or the contract is
   * unsigned) — never a guessed «no» (the `?signerMatches=` filter matches a `null`
   * row never).
   */
  signerMatches?: boolean | null;
  /**
   * ADDITIVE (v1.9.52, T.Z176) — «Зачислен»: the cat.2035.university mirror's own
   * word (the plain `status` of their `ticket_application`, mirrored verbatim into
   * `unti_applications.status`). TRI-STATE, always serialised: `true` = the literal
   * `accepted` (case-insensitive — the wire spells it lowercase; owner rule «Да =
   * ACCEPTED, нет = everything else»), `false` = every other literal, `null` = the
   * unti2035 sweep has not covered the row (no `unti_id`, or no received page has
   * named it) — never a guessed «нет» (the `?enrolled=` filter matches a `null` row
   * never). A LIVE MIRROR: their status change flips the fact on the next sweep.
   * NOT the T.Z111 enrollment pair — `enrollState` reads the iSmart `future`
   * platform's own `application.status`; a different system's word.
   */
  enrolled?: boolean | null;
  /**
   * ADDITIVE (v1.10.0, T.Z249 / TEC-1415) — «Распределён»: the CHILD's class fact,
   * the platform mirror first (`platform_distributions`, the worker's
   * distribution-sync pass) with our live study groups as the fallback. TRI-STATE:
   * `true` = a platform class OR one of our live groups; `false` = the mirror spoke
   * and names no class AND no our group; `null` = NO signal from either side —
   * render «—», never a guessed «нет» (the `?distributed=` filter matches a `null`
   * row never). Their advisory `is_distributed` never feeds this fact.
   */
  distributed?: boolean | null;
  // ---- ADDITIVE (v1.9.77, T.Z218) — the УНТИ entry exam off the row's leaf. ----
  /**
   * «Алгоритм — оценка»: the entry exam's algorithm grade, read off the row's УНТИ
   * leaf (`unti_applications.algorithm_grade` — their TEXT column, `'9.00'`-shaped,
   * parsed server-side) over the SAME leaf-winner fold the «Дедлайн договора» clock
   * and the «УНТИ API» verdict run. `null` = the mirror holds no parseable value —
   * render «—», never 0. The five keys below move together: wherever the api
   * resolved the leaf read (#3 on every sort path, #4, #5) all five travel, `null`
   * meaning «no such fact», never an absent key.
   */
  algorithmGrade?: number | null;
  /** ADDITIVE (v1.9.77, T.Z218) — «Язык — оценка» (see `algorithmGrade`). */
  languageGrade?: number | null;
  /** ADDITIVE (v1.9.77, T.Z218) — «Мотивация — оценка» (see `algorithmGrade`). */
  motivationGrade?: number | null;
  /**
   * ADDITIVE (v1.9.77, T.Z218) — «Сумма оценок»: the three grades summed in integer
   * hundredths; `null` unless ALL THREE parsed (a partial exam renders «—», never a
   * misleadingly low score). The value `?sortBy=gradeSum` orders the register and
   * the export by — nulls last both ways, ties by id. ADDITIVE (v1.9.78, T.Z219) —
   * the three grade keys sort on their OWN numbers the same way (`sortBy=
   * algorithmGrade` / `languageGrade` / `motivationGrade`), on the register and in
   * the export alike.
   */
  gradeSum?: number | null;
  /**
   * ADDITIVE (v1.9.77, T.Z218) — «Уровень сложности (УНТИ)»: the exam level of the
   * same leaf, VERBATIM (`elementary` / `advanced` — their lowercase literal, never
   * an enum on our side). `null` = the mirror says nothing. The column renders it
   * through the shared `untiDifficultyLabel` fold; the wire keeps the raw word.
   */
  difficultyLevel?: string | null;
  /**
   * ADDITIVE (v1.9.87, T.Z237) — «Трансферт»: the cat.2035 booking word of the SAME
   * leaf-winner fold (`unti_applications.transfert_status`, THEIR vocabulary,
   * VERBATIM — `activated` / `conditionally_activated` / `revoked` / `booked` seen
   * live; never an enum on our side). OPTIONAL as a key and the absence MEANS
   * something: the key travels only where the fold found a winner row (no leaf, or
   * a leaf the mirror does not know → key off); `null` = a winner row whose own word
   * is NULL — the «—» cell, never a guessed word. #3 resolves it on every sort
   * path; #4/#5 leave it off. `?transfert=` filters it (`none` or an exact value)
   * and #3.1 offers the distinct values.
   */
  transfertStatus?: string | null;
  // ---- ADDITIVE (v1.9.46, T.Z165; amended v1.9.59, T.Z187) — «Дедлайн договора», the
  // ---- derived contract clock: the УНТИ auto-decline date, fallback pass+21, computed
  // ---- server-side and never stored. ----
  /**
   * The moment the signed contract is due — the УНТИ auto-decline date
   * (`unti_applications.untill_dt` of the row's УНТИ leaf; УНТИ rejects the application
   * at that instant, so it is the real last moment to sign). A row the mirror says
   * nothing to (no leaf, no mirror row, or a null/unparseable date) keeps the old
   * arithmetic, `eviPassedAt + 21 календарных дней`. `null` = the clock is NOT running
   * — the row is signed (`contractSignedAt` says when) or not in `EVI_PASSED`; the
   * register renders «—», never a guessed deadline.
   */
  contractDeadlineAt: string | null;
  /**
   * Whole days to `contractDeadlineAt`, truncated towards zero; NEGATIVE = overdue
   * («просрочен на N дн.»), `0` = the deadline is today. `null` whenever
   * `contractDeadlineAt` is. The api owns the day math (one instant per page) — the
   * table only formats.
   */
  contractDaysLeft: number | null;
  epguStatusCode: string | null;
  status: ApplicationStatus;
  // ---- ADDITIVE (v1.9.15, T.Z86/T.Z88) — the two «дней» read models (#3). ----
  /**
   * Whole days in the CURRENT status, computed server-side (one instant per page);
   * `null` = the status start is unknown — render «—», never 0.
   */
  daysInStatus: number | null;
  /**
   * Whole days since the ЕВИ PASS event (`eviPassedAt`); keeps counting after the
   * status moves on. `null` = the child never passed — render «—», never 0.
   */
  daysSinceEvi: number | null;
  // ---- ADDITIVE (v1.9.48, T.Z168) — «Возраст», the derived whole-year age (#3). ----
  /**
   * Full years at READ time, derived server-side from the stored birth date
   * (migration 0039 — the unti /v1 feed's student birth date) over the same one
   * instant per page the two «дней» cells are. `null` = no birth date yet (the feed
   * has not touched the row) — render «—». The DATE itself never leaves the api: no
   * DTO carries it, and the export prints the age, never the date.
   */
  age: number | null;
  firstSeenAt: string;
  linkReceivedAt: string | null;
  eviPassedAt: string | null;
  updatedAt: string;
  // ---- T.F1: the rest of the ЕПГУ row (additive, migration 0006) — ЕПГУ-owned. ----
  submittedBy: string | null;
  submittedAt: string | null;
  statusUpdatedAt: string | null;
  isSpoStudent: boolean | null;
  gender: string | null;
  classCourse: string | null;
  specialty: string | null;
  specialtyCode: string | null;
  direction: string | null;
  orgName: string | null;
  courseName: string | null;
  format: CourseFormat | null;
  /**
   * ADDITIVE (v1.9.91, T.Z244), KEPT AS PROVENANCE (amended v1.9.93, T.Z245) — the
   * STORED ЕПГУ cell, always serialised by the api. NOT a gate: the venue rule (and
   * this page's «Площадка» control) judge the served `format` — the EFFECTIVE УНТИ
   * value, the api's own rule axis. This field stays so a reader can see WHERE the
   * served format diverges from the ЕПГУ file (the «переведён на онлайн, а в файле
   * офлайн» class). Optional HERE (an older payload or a stub may not carry it).
   */
  storedFormat?: CourseFormat | null;
  regionConfirmed: string | null;
  // ---- ADDITIVE (v1.9.94, T.Z247) — the distribution block («Школа» / «Класс/Группа» /
  // «Педагог», the админка's columns): the child's LIVE study-group link folded to the
  // group's venue, the group's own title and its teacher. Key-presence as v1.9.87:
  // the LIST (#3) carries the three keys always, the drawer/#5 do not; `null` =
  // «не распределён» (no live group) — or, for the teacher only, a group with no
  // teacher yet. The register's «Площадка» (`venueName`) stays its own fact.
  groupVenueName?: string | null;
  groupName?: string | null;
  groupTeacherName?: string | null;
  // ---- ADDITIVE (v1.8.2, T.Z0b, T.Z17) — the read-model «Статус менеджера» cell. ----
  /**
   * The LABEL of the journal's active winner (`removedAt IS NULL`, the greatest
   * `assignedAt`, tie → the greater journal id) — the one value the D8 display rule
   * shows, resolved server-side so the register prints it under the system status
   * with ONE batched read per page. `null` = no active row (the «без статуса» bucket
   * `?managerStatus=none` filters on); a removed row never surfaces here. Absent on
   * #5, which reads no journal — treat the key as optional everywhere.
   */
  managerStatus?: string | null;
}

export interface ApplicationEventDto {
  pguId: string | null;
  applicationId: number | null;
  type: ApplicationEventType;
  source: EventSource;
  actorType: EventActorType;
  actorUserId: number | null;
  field: string | null;
  /** Masked for a VIEWER when the field is a PII string (the timeline is a side door). */
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
}

// ADDITIVE (v1.8.0, Р9/D8, T.Y10) — the manager-status journal rides on #4 too; the
// row type itself lives in `./manager-statuses`.
export type ApplicationDetailDto = ApplicationDto & {
  events: ApplicationEventDto[];
  /** The #5.5 payload, additively — active rows first, then history. */
  managerStatuses?: ApplicationManagerStatusDto[];
  /**
   * ADDITIVE (v1.8.3, T.Z21) — «Адрес площадки» of the newest ЕВИ snapshot row for this
   * pgu, verbatim; the drawer paints it RED while `venueId` is null so an unmatched
   * row points at its own mismatching address. `null` = no ЕВИ row carries an address;
   * the key is optional (it never travels on #3/#5) — treat an absent key as `null`.
   */
  address?: string | null;
  /**
   * ADDITIVE (v1.9.5, T.Z52) — the study groups this child belongs to and their
   * педагоги. STAFF-ONLY: the api sets the key for an ADMIN/МЕНЕДЖЕР session and
   * leaves it off for every other role — an absent key means «not for you», an empty
   * array means «no group yet». Read-only; the cabinet's #26.12/#26.13 do the writing.
   */
  groups?: ApplicationGroupRefDto[];
};

/** ADDITIVE (v1.9.5, T.Z52) — one study group of a child, as the drawer reads it. */
export interface ApplicationGroupRefDto {
  id: number;
  name: string;
  venueId: number;
  venueName: string | null;
  teacherId: number | null;
  teacherName: string | null;
};

/** Contract #3 payload + its pagination block (`pageSize`, not the shared `limit`). */
export interface ApplicationListDto {
  applications: ApplicationDto[];
}
export type ApplicationListEnvelope = Envelope<ApplicationListDto>;
export type ApplicationDetailEnvelope = Envelope<ApplicationDetailDto>;

/** Body of #5. `status` is deliberately absent — the StatusEngine owns it. */
export interface ApplicationPatchInput {
  fullName?: string | null;
  phone?: string | null;
  email?: string | null;
  isOrganic?: boolean;
  agreementRejected?: boolean;
  /** ADDITIVE (v1.9.15, T.Z96) — the manual «не прошёл ЕВИ» flag; reversible. */
  eviFailedManually?: boolean;
  venueId?: number | null;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

/** The query #3 accepts, already encoded (see `applications-model.ts` for the parse). */
export interface ApplicationListQuery {
  /**
   * ONE funnel status or — ADDITIVE (v1.9.33, T.Z141) — a LIST of them: the api splits
   * the wire's comma form (`?status=LINK_RECEIVED,EVI_PASSED`, exactly what the ЦА cell
   * drill-down sends) and answers `status IN (…)`. `buildQuery` stringifies an array
   * into its comma spelling, so the caller passes the list as-is. ADDITIVE (v1.9.36,
   * T.Z143) — the VIRTUAL `EVI_FAILED` is a legal value too (the api ORs the «Провалил
   * ЕВИ» fact fold in); ADDITIVE (v1.9.66, T.Z204) — the SECOND virtual token
   * `EVI_NOT_PASSED` («Не прошёл ЕВИ», the ЕВИ-deadline fold) joins the same way. The
   * filter vocabulary lives in `applications-model.ts`.
   */
  status?:
    | ApplicationStatus
    | 'EVI_FAILED'
    | 'EVI_NOT_PASSED'
    | ReadonlyArray<ApplicationStatus | 'EVI_FAILED' | 'EVI_NOT_PASSED'>;
  venueId?: number;
  courseId?: number;
  // ---- ADDITIVE (v1.8.8, T.Z43): the programme/audience pair. ----
  /** A `programs.short_name` value («ИИ 8-9», «Python СПО», …); the api resolves it to
   *  its 1–2 course ids. An unknown value answers 400 VALIDATION. */
  programShort?: string;
  /** `8-9` | `10-11` | `СПО` — resolved through `programs.audience`, same IN-filter. */
  audience?: string;
  /**
   * ADDITIVE (v1.8.9, T.Z56) — «Отдел»: the venue's manager-department bucket, resolved
   * server-side into the venue-id set. Narrows the rows AND the pagination `total`.
   */
  department?: 'OP' | 'OS' | 'NONE' | 'MIXED';
  region?: string;
  hasVenue?: boolean;
  /**
   * Still in the frozen #3, so the api (and the export it narrows) keep honouring
   * it — but no web control sends it any more: A4/T.Z19 (2026-09-13) removed the
   * register's «Флаги» column and the «Органик» filter that went with it.
   */
  organic?: boolean;
  /** Additive (T.F1): a direct column filter since migration 0006 — see the api README.
   *  ADDITIVE (v1.9.19, T.Z99): the lowercase literal `none` is the «Формат не указан»
   *  bucket (`format IS NULL`). AMENDED (v1.9.79, T.Z220): #18's `appFormat` takes the
   *  literal too, so the export button forwards it instead of dropping it (the page
   *  remaps the key — `format` on #18 is the FILE format). */
  format?: CourseFormat | 'none';
  /**
   * ADDITIVE (v1.9.21, T.Z101) — the contract-fact bucket: `signed` / `not_signed` /
   * `not_arrived`. ADDITIVE (v1.9.24, T.Z111) — the enrollment pair joins the same
   * literal vocabulary: `enrolled` / `declined`. Lowercase literals on the wire (the
   * api's pattern arm, five values), ANDed with every other filter. #18 accepts the
   * same key, so the export button sends it too.
   */
  contract?: 'signed' | 'not_signed' | 'not_arrived' | 'enrolled' | 'declined';
  /**
   * ADDITIVE (v1.9.49, T.Z169) — «Согласие родителей»: `true` / `false`, the api's
   * own lowercase literals. A row with `legalRepConsent: null` matches NEITHER
   * value («нет данных» is never folded into a «no»). #18 accepts the same key,
   * so the export button sends it too; no new file column — the owner asked card +
   * filter only.
   */
  legalRepConsent?: boolean;
  /**
   * ADDITIVE (v1.9.51, T.Z172) — «Договор заключен ПП?»: `true` / `false`, the api's
   * own lowercase literals. A row with `signerMatches: null` (no verdict — no signer,
   * no our name, or unsigned) matches NEITHER value. #18 accepts the same key, so the
   * export button sends it too; no new file column — the owner asked card + filter
   * only.
   */
  signerMatches?: boolean;
  /**
   * ADDITIVE (v1.9.52, T.Z176) — «Зачислен»: `true` / `false`, the api's own
   * lowercase literals. A row with `enrolled: null` (the unti2035 sweep has not
   * covered it) matches NEITHER value. #18 accepts the same key, so the export
   * button sends it too; no new file column — the owner asked card + filter only.
   */
  enrolled?: boolean;
  /**
   * ADDITIVE (v1.10.0, T.Z249 / TEC-1415) — «Распределён»: `true` / `false`, the
   * api's own lowercase literals. A row with NO signal (no mirror row, no our live
   * group) matches NEITHER value. #18 accepts the same key, so the export button
   * sends it too (the file gains the appended «Распределён» column).
   */
  distributed?: boolean;
  /**
   * ADDITIVE (v1.9.25, T.Z113) — the auto «Провалил ЕВИ» fact: `true` = set,
   * `false` = explicitly clear, absent = no filter. #18 accepts the same key, so
   * the export button sends it too (the file gains the «Провалил ЕВИ» column).
   */
  eviFailed?: boolean;
  /**
   * ADDITIVE (v1.9.48, T.Z168) — the «Возраст» bucket, three lowercase literals on
   * the wire (`under14` = «Меньше 14 лет», `14to18` = «От 14 до 18», `over18` =
   * «От 18»), ANDed with every other filter. #18 accepts the same key, so the
   * export button sends it too (the file gains the APPENDED «Возраст» column). A row
   * with no birth date yet matches NO bucket.
   * ADDITIVE (v1.9.50, T.Z170) — the FOURTH literal `unknown` («Не заполнен») is the
   * undated rows' own bucket (`birth_date IS NULL`): the rows the feed simply does
   * not know, now reachable — and exported — with one click.
   * T.Z181 (v1.9.54) — the BOUNDARIES moved, the wire did not: `14to18` answers
   * 14 ≤ age < 18 and `over18` age ≥ 18 — an exactly-18-year-old is `over18`.
   */
  ageGroup?: 'under14' | '14to18' | 'over18' | 'unknown';
  /**
   * ADDITIVE (v1.9.56, T.Z186) — «Рассылка»: a Список рассылок id (#34). The api
   * narrows to the list's CURRENT recipients (an ACTIVE snapshot row — `removed_at`
   * null), so the register shows who the list would mail today; the «Выбывшие» are the
   * list's own exclusion. AND-combined with every other filter. #18 accepts the same
   * key, so the export button sends it too; no new file column.
   */
  mailingList?: number;
  /**
   * ADDITIVE (v1.9.56, T.Z186) — «FTP»: `true` = «есть» (`unti_id IS NOT NULL` — the
   * УНТИ key the FTP sync filled), `false` = «нет» (`unti_id IS NULL`). The two values
   * partition the register. #18 accepts the same key, so the export button sends it
   * too; no new file column.
   */
  ftp?: boolean;
  /**
   * ADDITIVE (v1.9.66, T.Z203) — «УНТИ API»: the row's linkage to the УНТИ mirror,
   * four CASE-SENSITIVE lowercase literals on the wire. `found` = the mirror's
   * LATEST row for the row's leaf (greatest `applied_at`, tie → the largest
   * `application_id`) carries the row's own `pgu_id`; `mismatch` = it names a
   * different one; `unti_not_found` = the leaf is absent from the mirror; `no_unti`
   * = the row carries no `unti_id`. The four values partition the register; the
   * api narrows in two phases (the mirror is a BY-VALUE join) and #18 takes the
   * same key, so the export button sends it too; no new file column.
   */
  untiApi?: 'found' | 'mismatch' | 'unti_not_found' | 'no_unti';
  /**
   * ADDITIVE (v1.9.87, T.Z237) — «Трансферт»: the literal `none` (the «no value»
   * bucket — no leaf, no mirror row, a NULL winner word) or one of the values the
   * #3.1 facet (`GET /api/applications/transfert-values`, {@link useTransfertValues})
   * offers, matched against the leaf-winner's `transfert_status` EXACTLY (verbatim,
   * case-sensitive; anything else answers 400 — the `?untiApi=` grammar). The api
   * narrows in two phases and #18 takes the same key, so the export button sends it
   * too (the file gains the appended «Трансферт» column).
   */
  transfert?: string;
  q?: string;
  // ---- ADDITIVE (v1.8.0, Р9/#3, T.Y10): the «Статус менеджера» filters. ----
  /** `none` — the «без статуса» bucket: no ACTIVE row in the journal. Combining it
   *  with `managerStatusId` answers 400, so the model never sends both. */
  managerStatus?: 'none';
  /** Only applications whose journal holds an ACTIVE row of this dictionary id. */
  managerStatusId?: number;
  page?: number;
  pageSize?: number;
  /**
   * The frozen v1.1 `sort=<field>[.<dir>]` parameter — still in the contract, but the
   * register no longer sends it: since v1.9.3 (T.Z59) the header clicks speak
   * `sortBy`/`order` below. Kept on the type because #3 still answers it and a caller
   * may pin a legacy view verbatim.
   */
  sort?: string;
  /**
   * ADDITIVE (v1.9.3, T.Z59) — the column the register sorts on: ONE value per rendered
   * column (`pguId`, `fullName`, `phone`, `email`, `status`, `venue`, `courseId`,
   * `submittedAt`, `classCourse`, `format`, `eviDeadline`, `updatedAt`). The api sorts
   * the WHOLE filtered set BEFORE the page is cut, so page 1 shows the globally
   * smallest/largest row. An unknown value answers 400 — the model never sends one.
   */
  sortBy?: string;
  /** ADDITIVE (v1.9.3, T.Z59) — the direction of `sortBy`: `asc` (default) or `desc`.
   *  Never sent without `sortBy` (the api answers 400 to a lone `order`). */
  order?: 'asc' | 'desc';
}

/** The `format` vocabulary #3 accepts (the api normalises case, but we send it clean). */
export const FORMAT_FILTERS: ReadonlyArray<CourseFormat> = ['ONLINE', 'OFFLINE'];

export function applicationsPath(query: ApplicationListQuery = {}): string {
  return `/applications${buildQuery(query)}`;
}

export function applicationDetailPath(pguId: string): string {
  return `/applications/${encodeURIComponent(pguId)}`;
}

export async function fetchApplications(
  query: ApplicationListQuery,
  signal?: AbortSignal,
): Promise<ApplicationListEnvelope> {
  return requestEnvelope<ApplicationListDto>('GET', applicationsPath(query), ['data'], { signal });
}

export async function fetchApplicationDetail(
  pguId: string,
  signal?: AbortSignal,
): Promise<ApplicationDetailEnvelope> {
  return requestEnvelope<ApplicationDetailDto>('GET', applicationDetailPath(pguId), ['data'], { signal });
}

export async function patchApplication(
  pguId: string,
  body: ApplicationPatchInput,
  signal?: AbortSignal,
): Promise<ApplicationDto> {
  const envelope = await requestEnvelope<ApplicationDto>('PATCH', applicationDetailPath(pguId), ['data'], {
    // `method` is set in BOTH places on purpose: `apiRequest` spreads `init`
    // into the fetch call and currently never forwards its own `method`
    // argument, so a body without this key would travel as a GET and the
    // browser would refuse it ("GET/HEAD cannot have body"). Harmless
    // duplication here; the argument stays for when client.ts forwards it.
    method: 'PATCH',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return envelope.data;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Cache identity of one list slice — every filter, the sort and the page are part of it. */
export type ApplicationListQueryKey = readonly ['applications', 'list', ApplicationListQuery];

/**
 * The ROOT of every list slice. `applicationsQueryKey` narrows under it per filter,
 * sort and page; the writes invalidate by this prefix, so one call reaches every
 * loaded slice (the prefix match is the whole point — see
 * `managerStatusWriteInvalidationKeys` for the #5.6 half of the story).
 */
export const APPLICATIONS_LIST_ROOT = ['applications', 'list'] as const;

export function applicationsQueryKey(query: ApplicationListQuery): ApplicationListQueryKey {
  return [...APPLICATIONS_LIST_ROOT, query] as const;
}

/**
 * #3 — one page of the register. `keepPreviousData` keeps the table mounted while
 * a filter change is in flight (no flash to the skeleton), `refetchInterval` keeps
 * the register current — a manager leaves this page open on a second screen.
 */
export function useApplications(query: ApplicationListQuery) {
  return useQuery({
    queryKey: applicationsQueryKey(query),
    queryFn: ({ signal }) => fetchApplications(query, signal),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: 0,
  });
}

/**
 * ADDITIVE (v1.9.56, T.Z186) — the mailing-list NAMES the register's «Рассылка» select
 * offers (#34.1, `GET /api/mailing-lists`, the SAME unpaginated index the mailings page
 * reads — the owner speaks in list names, never in ids). The hook lives beside #3's own
 * hooks, not in `./mailing-lists`, because the register needs a DIFFERENT read profile
 * than the tab that sends letters: the index is a MANAGER+ surface, so a VIEWER session
 * answers 403 — ONE quiet refusal (`retry: 0`, the `useManagerStatusDictionary`
 * precedent), the select renders «Все» alone and the page is otherwise untouched. The
 * key is suffixed so this read keeps its own cache entry instead of arguing with the
 * mailings tab's observer over options. A body without a list array renders `[]` (the
 * same shape guard `useMailingLists` plays), never a crash.
 */
export function useMailingListIndex() {
  return useQuery({
    queryKey: ['mailing-lists', 'index', 'register'],
    queryFn: async ({ signal }) => {
      const answer = await requestEnvelope<{ lists?: unknown }>('GET', MAILING_LISTS_ROOT, ['data'], { signal });
      const lists = Array.isArray(answer.data?.lists) ? (answer.data.lists as Array<{ id?: unknown; name?: unknown }>) : [];
      return lists
        .filter((list) => typeof list?.id === 'number' && typeof list?.name === 'string')
        .map((list) => ({ id: list.id as number, name: list.name as string }));
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

/** Cache identity of one profile (the optimistic update of #5 writes here). */
export function applicationDetailQueryKey(pguId: string): readonly [string, string, string] {
  return ['applications', 'detail', pguId];
}

/**
 * ADDITIVE (v1.9.87, T.Z237) — the #3.1 facet root (`GET /api/applications/transfert-values`):
 * the path the register's «Трансферт» select reads. A sibling of the list, not a
 * parameter of it — the vocabulary travels once per page load, not per row.
 */
export const TRANSFERT_VALUES_ROOT = '/applications/transfert-values';

/**
 * ADDITIVE (v1.9.87, T.Z237) — the DISTINCT «Трансферт» words the register's
 * winner fold can match (#3.1, `{ data: { values: string[] } }`), the options the
 * «Трансферт» select offers. The same read profile the «Рассылка» index keeps:
 * staleTime 5 min (a vocabulary that moves hourly, not per keystroke), `retry: 0`
 * so a failing read degrades to the «Все»/`none` options alone — ONE quiet
 * refusal, the page otherwise untouched. A body without an array renders `[]`
 * (the same shape guard `useMailingListIndex` plays), never a crash. The api
 * scopes the answer to the caller's venue view, so a scoped manager is offered
 * only words their own rows can match.
 */
export function useTransfertValues() {
  return useQuery({
    queryKey: ['applications', 'transfert-values'],
    queryFn: async ({ signal }) => {
      const answer = await requestEnvelope<{ values?: unknown }>('GET', TRANSFERT_VALUES_ROOT, ['data'], { signal });
      const values = Array.isArray(answer.data?.values) ? answer.data.values : [];
      return values.filter((value): value is string => typeof value === 'string');
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

/** #4 — one application plus its timeline. */
export function useApplicationDetail(pguId: string | null) {
  return useQuery({
    queryKey: applicationDetailQueryKey(pguId ?? ''),
    queryFn: ({ signal }) => fetchApplicationDetail(pguId as string, signal),
    enabled: pguId !== null,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: 0,
  });
}

// ---------------------------------------------------------------------------
// T.Z20/T.Z20b — #3 as a counter (the register's «несвязанные» alert)
// ---------------------------------------------------------------------------

/**
 * Cache identity of one COUNT slice. Deliberately its own key family, NOT the
 * register's `['applications', 'list', …]`: the count fetches a page of one, and
 * sharing the register's key would serve that one-row answer to the table.
 */
export type ApplicationsCountQueryKey = readonly ['applications', 'count', ApplicationListQuery];

/** The prefix every count slice sits under — what a write invalidates. */
export const APPLICATIONS_COUNT_ROOT = ['applications', 'count'] as const;

export function applicationsCountQueryKey(query: ApplicationListQuery): ApplicationsCountQueryKey {
  return [...APPLICATIONS_COUNT_ROOT, query] as const;
}

/**
 * `GET /api/applications?<query>&page=1&pageSize=1` read for its `meta.total` —
 * the cheapest count contract #3 offers, and the reason the register's
 * «несвязанные» alert needs NO new aggregate (contract unchanged, wave 10; the
 * dashboard card the hook was built for went away in T.Z20b).
 *
 * The query MUST carry the same filters the alert drills into — today
 * `{ hasVenue: false, format: 'OFFLINE', status }` — because "the alert and the
 * filter agree" is a request semantics claim: the alert prints exactly what `#3`
 * would answer that link with (pagination keys do not move a `meta.total`).
 * Caching, polling and staleness follow the register's own list query, so the
 * banner and the table it sits above age together.
 */
export function useApplicationsCount(
  query: ApplicationListQuery,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: applicationsCountQueryKey(query),
    queryFn: ({ signal }) => fetchApplications({ ...query, page: 1, pageSize: 1 }, signal),
    // The alert prints a number, not an envelope — the unwrapping lives here so a
    // payload without `meta` (an older build) reads as "nothing to show", never as 0.
    select: (envelope) => envelope.meta?.total,
    // T.Z20b — the reader decides: the register's alert asks for the counts only
    // for the roles that can act on them, so a curator's session never sends them.
    enabled: options.enabled ?? true,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    retry: 0,
  });
}

/**
 * #5 — manual repair. Optimistic: the toggled flag / filled field lands in the
 * profile immediately, the timeline catches up when the invalidation resolves (the
 * api appends the events in the same transaction as the update). A failure rolls
 * the profile back and surfaces the api's message (`403` on a sourced PII field).
 */
export function usePatchApplication(pguId: string) {
  const queryClient = useQueryClient();
  const detailKey = applicationDetailQueryKey(pguId);

  return useMutation({
    mutationFn: (body: ApplicationPatchInput) => patchApplication(pguId, body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<ApplicationDetailEnvelope>(detailKey);
      queryClient.setQueryData<ApplicationDetailEnvelope>(detailKey, (current) =>
        current ? { ...current, data: optimisticDetail(current.data, body) } : current,
      );
      return { previous };
    },
    onError: (_error, _body, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous);
    },
    onSettled: () => {
      // The server's answer is the authority: the timeline and the list row
      // (`updatedAt`, the venue name) both come from the same change.
      void queryClient.invalidateQueries({ queryKey: detailKey });
      void queryClient.invalidateQueries({ queryKey: APPLICATIONS_LIST_ROOT });
      // T.Z20b — the venue link is exactly what the «несвязанные» alert counts:
      // an invalidated list root would leave the banner standing over a register
      // whose every row is linked until the next poll. The count root is its own
      // key family, so it needs its own invalidation.
      void queryClient.invalidateQueries({ queryKey: APPLICATIONS_COUNT_ROOT });
    },
  });
}

/**
 * The optimistic half of the PATCH semantics (docs/05 §4): only provided keys
 * change, a venue link flips `venueMatchStatus` to MANUAL and an unlink back to
 * UNMATCHED. Pure, so the unit suite pins it without a DOM — and the page never
 * guesses a shape the api would then correct out from under it.
 */
export function optimisticDetail(
  current: ApplicationDetailDto,
  patch: ApplicationPatchInput,
): ApplicationDetailDto {
  const next: ApplicationDetailDto = { ...current };

  for (const field of ['fullName', 'phone', 'email'] as const) {
    const value = patch[field];
    if (value !== undefined) next[field] = value;
  }
  for (const flag of ['isOrganic', 'agreementRejected', 'eviFailedManually'] as const) {
    const value = patch[flag];
    if (value !== undefined) next[flag] = value;
  }
  if (patch.venueId !== undefined && patch.venueId !== current.venueId) {
    next.venueId = patch.venueId;
    next.venueMatchStatus = patch.venueId === null ? 'UNMATCHED' : 'MANUAL';
    // `venueName` is the join the api re-resolves; the optimistic view clears it
    // rather than showing the PREVIOUS venue's name against the new id.
    if (patch.venueId === null) next.venueName = null;
  }
  return next;
}
